import { CommonModule, formatDate } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatNativeDateModule } from "@angular/material/core";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import {
  MatDatepickerInputEvent,
  MatDatepickerModule,
} from "@angular/material/datepicker";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { SettingData } from "../../../models/settings.model";
import { GlobalVariablesService } from "../../../global.service";
import { APIService } from "../../../fleet-management/backend-service/backend-service";
import { ActivatedRoute, Router } from "@angular/router";
import { SharedService } from "../../../share-data/shared.service";
import { HttpResponse } from "@angular/common/http";
import { NotificationService } from "../../../shared/services/notification.service";
import { LoadingService } from "../../../services/loading.service";
import { Subscription } from "rxjs";

export interface CustomerList {
  id: string;
  name: string;
}
export interface ConsigneeList {
  id: string;
  name: string;
}

@Component({
  selector: "app-order-booking",
  imports: [
    FormsModule,
    MatAutocompleteModule,
    MatIconModule,
    MatFormFieldModule,
    MatExpansionModule,
    MatInputModule,
    MatRadioModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    ReactiveFormsModule,
    CommonModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: "./order-booking.component.html",
  styleUrl: "./order-booking.component.scss",
})
export class OrderBookingComponent implements OnDestroy {
  isLoading: boolean = false;
  private loadingSubscription: Subscription;
  @ViewChild("searchInput") searchInput!: ElementRef<HTMLInputElement>;
  customerLocations = [{ id: "", address: "", locationPin: "" }];
  pickUpLocationSelectedValue = {
    id: "",
    address: "",
    locationPin: "",
    contacts: [{ id: "", name: "", phone: "", position: "" }],
  };
  deliveryLocationOptions = [
    { id: "", address: "", locationPin: "", contacts: [] },
  ];
  filteredConsigneeDeliveryLocationListOptions = [
    { id: "", address: "", locationPin: "", contacts: [] },
  ];
  deliveryLocationSelectedValue = {
    id: "",
    address: "",
    locationPin: "",
    contacts: [{ id: "", name: "", phone: "", position: "" }],
  };
  // dimensionOptions: string[] = ['Case', 'Box', 'Cubic Meter', 'Ton'];
  dimensionOptions: SettingData[] = [];
  dimensionSelectedValue = { id: "", name: "" };
  packagingTypeOptions: SettingData[] = [];
  temperatureControlOptions: SettingData[] = [];
  handlingTypeOptions: SettingData[] = [];
  commodityTotals = [
    {
      customerRef: "",
      description: "",
      quantity: "",
      packagingType: { id: "", name: "" },
      length: "",
      width: "",
      height: "",
      volume: "",
      grossWeight: "",
      stackable: false,
      dangerousFlag: false,
      temperatureControl: { id: "", name: "" },
      handlingType: { id: "", name: "" },
    },
  ];

  loadingMethodOptions: SettingData[] = [];
  filterLoadingMethodList: SettingData[] = [];
  loadingMethodSelectedValue = { id: "", name: "" };
  unloadingMethodOptions: SettingData[] = [];
  filterUnloadingMethodList: SettingData[] = [];
  unloadingMethodSelectedValue = { id: "", name: "" };
  customerName!: string;
  commodity!: string;
  totalTruckOrdered!: string;
  truckCountMismatch: boolean = false;
  quantity!: string;
  currentLocation: google.maps.LatLngLiteral | null = null;
  truckTypes: SettingData[] = [];
  filterTruckTypesList: SettingData[][] = [];
  // filterTruckTypesList: any[][] = [];

  contactForm!: FormGroup;
  orderTruckForm!: FormGroup;
  pickupDate: string = "";
  deliveryDate: string = "";
  customer!: CustomerList;
  customers: CustomerList[] = [];
  customerListOptions = [{ id: "", name: "" }];
  filteredCustomerListOptions = [{ id: "", name: "" }];

  pickUpLocationOptions = [
    { id: "", address: "", locationPin: "", contacts: [] },
  ];
  filteredCustomerPickUpLocationListOptions = [
    { id: "", address: "", locationPin: "", contacts: [] },
  ];

  customerSelectedValue = { id: "", name: "" };

  consignee!: ConsigneeList;
  consignees: ConsigneeList[] = [];
  consigneeListOptions = [{ id: "", name: "" }];
  filteredConsigneeListOptions = [{ id: "", name: "" }];
  consigneeSelectedValue = { id: "", name: "" };
  pickUpContactList = [{ name: "", phone: "", position: "" }];
  showPickUpContact: boolean = false;
  showDeliveryContact: boolean = false;
  deliveryContactList = [{ name: "", phone: "", position: "" }];

  isEdit: string = "";
  isReadOnly: boolean = false;
  isDraft: boolean = false;
  userData: any;
  assignedTrucks: { id: number; plateNumber?: string; truckType?: string; driverName?: string; driverPhone?: string }[] = [];
  customerExpanded: boolean = true;
  consigneeExpanded: boolean = true;
  commodityExpanded: boolean = true;
  transportExpanded: boolean = true;

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private globalVariable: GlobalVariablesService,
    private apiService: APIService,
    private route: ActivatedRoute,
    private sharedService: SharedService,
    private router: Router,
    private notification: NotificationService,
    public loadingService: LoadingService
  ) {
    this.loadingSubscription = this.loadingService.isLoading$.subscribe(
      (loading) => (this.isLoading = loading)
    );
  }

  ngOnDestroy() {
    if (this.loadingSubscription) {
      this.loadingSubscription.unsubscribe();
    }
  }

  ngOnInit() {
    this.pickUpContactList = [];
    this.getCustomerList();
    this.getConsigneeList();
    this.setDimensions();
    this.setTypeOfTruck();
    this.setHandlingMethod();
    this.setPackagingTypes();
    this.setTemperatureControlOptions();
    this.setHandlingTypeOptions();

    // Set default values for the first commodity
    this.commodityTotals[0].packagingType = this.packagingTypeOptions[0];
    this.commodityTotals[0].temperatureControl =
      this.temperatureControlOptions[0];
    this.commodityTotals[0].handlingType = this.handlingTypeOptions[0];
    this.contactForm = this.fb.group({
      pickupContact: this.fb.array([this.createContactForm()]),
      deliveryContact: this.fb.array([this.createContactForm()]),
    });
    this.orderTruckForm = this.fb.group({
      truckOrdered: this.fb.array([this.createTruckForm()]),
    });

    this.route.queryParams.subscribe(async (params) => {
      this.isEdit = params["isEdit"];
      this.isReadOnly = params["isReadOnly"] === "true";
      if (this.isEdit == "true") {
        try {
          this.userData = this.sharedService.getData();
          console.log("======>Share data:", this.userData);

          // Map cargoItems from API response to commodityTotals
          const cargoItems =
            this.userData.cargoItems || this.userData.commodities || [];
          this.commodityTotals = cargoItems.map((c: any) => {
            // Get the packaging type ID (could be from packagingType or packagingType field)
            const packagingType = c.packagingType || c.packagingType;
            const temperatureControlId =
              c.temperatureControl || c.temperatureControlId;
            const handlingTypeId = c.handlingType || c.handlingTypeId;

            const numVal = (v: any) => (v != null && Number(v) !== 0 ? v : "");
            return {
              customerRef: c.customerRef || "",
              description: c.description || "",
              quantity: numVal(c.quantity),
              packagingType: this.packagingTypeOptions.find(
                (opt) => opt.id == packagingType
              ) ||
                this.packagingTypeOptions[0] || { id: "", name: "" },
              length: numVal(c.length ?? c.lengthCm),
              width: numVal(c.width ?? c.widthCm),
              height: numVal(c.height ?? c.heightCm),
              volume: numVal(c.volume ?? c.volumeM3),
              grossWeight: numVal(c.grossWeight ?? c.grossWeightKg),
              stackable: c.stackable || false,
              dangerousFlag: c.dangerousFlag || c.dgFlag || false,
              temperatureControl: this.temperatureControlOptions.find(
                (opt) => opt.id == temperatureControlId
              ) ||
                this.temperatureControlOptions[0] || { id: "", name: "" },
              handlingType: this.handlingTypeOptions.find(
                (opt) => opt.id == handlingTypeId
              ) ||
                this.handlingTypeOptions[0] || { id: "", name: "" },
            };
          });

          // Ensure at least one empty row is present if no commodities exist
          if (this.commodityTotals.length === 0) {
            this.commodityTotals = [
              {
                customerRef: "",
                description: "",
                quantity: "",
                packagingType: this.packagingTypeOptions[0] || {
                  id: "",
                  name: "",
                },
                length: "",
                width: "",
                height: "",
                volume: "",
                grossWeight: "",
                stackable: false,
                dangerousFlag: false,
                temperatureControl: this.temperatureControlOptions[0] || {
                  id: "",
                  name: "",
                },
                handlingType: this.handlingTypeOptions[0] || {
                  id: "",
                  name: "",
                },
              },
            ];
          }

          this.isDraft = this.userData["isDraft"] === true;
          this.assignedTrucks = (this.userData["assignedTrucks"] || []).filter((t: any) => t.plateNumber);
          this.totalTruckOrdered = this.userData["numberOfTrucksOrder"];
          this.loadingMethodSelectedValue =
            this.globalVariable.SETTING_HANDLING_METHODS.find(
              (method: any) => method.id === this.userData["loadingMethod"]
            );
          this.unloadingMethodSelectedValue =
            this.globalVariable.SETTING_HANDLING_METHODS.find(
              (method: any) => method.id === this.userData["unloadingMethod"]
            );
          const today = formatDate(new Date(), "yyyy-MM-dd", "en-US");
          const validDate = (d: any) => (d && new Date(d).getFullYear() > 1970 ? d : today);
          this.pickupDate = validDate(this.userData["pickupDate"]);
          this.deliveryDate = validDate(this.userData["deliveryDate"]);
          // this.orderTruckForm.value.truckOrdered = this.userData["truckTypesLoaded"];

          // FIXED: Properly map truck types for edit mode
          // First, clear the existing FormArray
          while (this.truckOrdered.length !== 0) {
            this.truckOrdered.removeAt(0);
          }

          // Then populate with the edit data
          this.userData["truckTypesLoaded"].forEach((truckData: any) => {
            // Find the truck type object from globalVariable
            const truckTypeObj = this.globalVariable.SETTING_TRUCK_TYPES.find(
              (truck: any) => truck.id === truckData.truckType
            );
            console.log(
              "Mapping truck type:",
              truckData.truckType,
              "Value_Mapped",
              truckTypeObj
            );
            // Create and add the form group with proper mapping
            const truckFormGroup = this.fb.group({
              truckType: [truckTypeObj || ""], // This will show the name in the dropdown
              numberOfTruck: [truckData.numberOfTruck || ""],
            });

            this.truckOrdered.push(truckFormGroup);
          });
          this.filterTruckTypesList = [];
          for (let i = 0; i < this.truckOrdered.length; i++) {
            this.filterTruckTypesList[i] = [...this.truckTypes];
          }

          console.log("filterTruckTypesList:", this.filterTruckTypesList);

          this.cdr.detectChanges(); // Ensure changes are reflected
        } catch (error) {}
      }
    });
  }

  getCustomerList() {
    this.customerListOptions = [];
    this.filteredCustomerListOptions = [];
    this.pickUpContactList = [];
    let loginData = JSON.parse(localStorage.getItem("loginData")!);
    console.log("LoginData: ", loginData);
    this.apiService.get(`/api/customers/list`, loginData.token).subscribe({
      next: (response) => {
        console.log(
          "/api/customer/list" + ",\n" + "Customer_List_Response:",
          response.body
        );
        if (response.status === 200) {
          response.body.customers.forEach((data: { [x: string]: any }) => {
            try {
              this.customer = data as CustomerList;
              console.log("Data_Response: ", this.customer.name);
              this.customerListOptions.push({
                id: this.customer.id,
                name: this.customer.name,
              });
              console.log(
                "======> Customer Options: ",
                this.customerListOptions
              );
              if (this.isEdit == "true") {
                const rawPickup = this.userData["pickupDate"];
                this.pickupDate = (rawPickup && new Date(rawPickup).getFullYear() > 1970)
                  ? formatDate(rawPickup, "yyyy-MM-dd", "en-US")
                  : formatDate(new Date(), "yyyy-MM-dd", "en-US");
                this.customerSelectedValue = this.customerListOptions.find(
                  (customer: any) => customer.id === this.userData["customerId"]
                ) || { id: "", name: "" };
                this.getCustomerLocationById(this.userData["customerId"]);
              }
            } catch (error) {
              console.error("Error parsing customer data:", error);
            }
          });
          this.filteredCustomerListOptions = [...this.customerListOptions];
        } else {
          console.log("Hey you");
        }
      },
      error: (error) => {
        console.error("Error status:", error.status);
        console.error("Error message:", error.message);
      },
    });
  }

  onCustomerSelected(event: any) {
    console.log("Selected value:", event.value); // This will log the selected object
    console.log("Customer ID:", event.value.id); // Access a specific property
    this.getCustomerLocationById(event.value.id);
  }
  onOpenedChange(opened: boolean) {
    if (opened) {
      setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
    }
  }

  filterCustomerOptions(event: Event) {
    const v = (event.target as HTMLInputElement).value?.toLowerCase() ?? "";
    this.filteredCustomerListOptions = this.customerListOptions.filter((o) =>
      o.name.toLowerCase().includes(v)
    );
  }
  filterConsigneeOptions(event: Event) {
    const v = (event.target as HTMLInputElement).value?.toLowerCase() ?? "";
    this.filteredConsigneeListOptions = this.consigneeListOptions.filter((o) =>
      o.name.toLowerCase().includes(v)
    );
  }
  filteredCustomerPickUpLocationOptions(event: Event) {
    const v = (event.target as HTMLInputElement).value?.toLowerCase() ?? "";
    this.filteredCustomerPickUpLocationListOptions =
      this.pickUpLocationOptions.filter((o) =>
        o.address.toLowerCase().includes(v)
      );
  }
  filteredConsigneePickUpLocationOptions(event: Event) {
    const v = (event.target as HTMLInputElement).value?.toLowerCase() ?? "";
    this.filteredConsigneeDeliveryLocationListOptions =
      this.deliveryLocationOptions.filter((o) =>
        o.address.toLowerCase().includes(v)
      );
  }
  // filterTruckTypesFunc(event: Event) {
  //   const v = (event.target as HTMLInputElement).value?.toLowerCase() ?? '';
  //   this.filterTruckTypesList = this.truckTypes.filter(
  //     o => o.name.toLowerCase().includes(v)
  //   );
  // }
  filterTruckTypesFunc(event: any, index: number) {
    const search = event.target.value.toLowerCase();
    this.filterTruckTypesList[index] = this.truckTypes.filter((t) =>
      t.name.toLowerCase().includes(search)
    );
  }
  filterLoadingMethodFunc(event: any) {
    const search = event.target.value.toLowerCase();
    this.filterLoadingMethodList = this.loadingMethodOptions.filter((t) =>
      t.name.toLowerCase().includes(search)
    );
  }
  filterUnloadingMethodFunc(event: any) {
    const search = event.target.value.toLowerCase();
    this.filterUnloadingMethodList = this.unloadingMethodOptions.filter((t) =>
      t.name.toLowerCase().includes(search)
    );
  }

  getCustomerLocationById(id: string) {
    this.pickUpLocationOptions = [];
    this.filteredCustomerPickUpLocationListOptions = [];
    let loginData = JSON.parse(localStorage.getItem("loginData")!);
    this.apiService
      .get(`/api/orders/locations/customer/${id}`, loginData.token)
      .subscribe({
        next: (response) => {
          console.log(
            `/api/orders/locations/customer/${id}` +
              ",\n" +
              "Customer_Location:",
            response.body.locations
          );
          if (response.status === 200) {
            response.body.locations.forEach((data: { [x: string]: any }) => {
              if (data["id"] != null) {
                try {
                  this.pickUpLocationOptions.push({
                    id: data["id"],
                    address: data["address"],
                    locationPin: data["locationPin"],
                    contacts: data["contacts"],
                  });
                  if (this.isEdit == "true") {
                    this.pickUpContactList = [];
                    this.pickUpLocationSelectedValue =
                      this.pickUpLocationOptions.find(
                        (location: any) =>
                          location.id === this.userData["pickupLocationId"]
                      ) || {
                        id: "",
                        address: "",
                        locationPin: "",
                        contacts: [],
                      };
                    this.pickUpLocationSelectedValue.contacts.forEach(
                      (contact: any) => {
                        this.pickUpContactList.push({
                          name: contact["name"],
                          phone: contact["phone"],
                          position: contact["position"],
                        });
                        this.showPickUpContact = true;
                      }
                    );
                  }
                } catch (error) {
                  console.error("Error parsing customer data:", error);
                }
              }
            });
            this.filteredCustomerPickUpLocationListOptions = [
              ...this.pickUpLocationOptions,
            ];
          } else {
            console.log("Hey you");
          }
        },
        error: (error) => {
          console.error("Error status:", error.status);
          console.error("Error message:", error.message);
        },
      });
  }
  onCustomerLocationSelected(event: any) {
    this.pickUpContactList = [];
    this.pickUpLocationSelectedValue = event.value;
    event.value.contacts.forEach((contact: any) => {
      this.pickUpContactList.push({
        name: contact["name"],
        phone: contact["phone"],
        position: contact["position"],
      });
    });
    if (this.pickUpContactList.length > 0) {
      this.showPickUpContact = true;
    } else {
      this.showPickUpContact = false;
    }
    console.log("this.pickUpContactList: ", this.pickUpContactList);
  }

  getConsigneeList() {
    this.consigneeListOptions = [];
    this.filteredConsigneeListOptions = [];
    let loginData = JSON.parse(localStorage.getItem("loginData")!);
    console.log("LoginData: ", loginData);
    this.apiService.get(`/api/consignees/list`, loginData.token).subscribe({
      next: (response) => {
        console.log(
          "/api/consignees/list" + ",\n" + "Consignee_List_Response:",
          response.body
        );
        if (response.status === 200) {
          response.body.consignees.forEach((data: { [x: string]: any }) => {
            try {
              this.consignee = data as ConsigneeList;
              console.log("Data_Response: ", this.consignee.name);
              this.consigneeListOptions.push({
                id: this.consignee.id,
                name: this.consignee.name,
              });
              if (this.isEdit == "true") {
                const rawDelivery = this.userData["deliveryDate"];
                this.deliveryDate = (rawDelivery && new Date(rawDelivery).getFullYear() > 1970)
                  ? formatDate(rawDelivery, "yyyy-MM-dd", "en-US")
                  : formatDate(new Date(), "yyyy-MM-dd", "en-US");
                console.log("+++++++++++Delivery Date: ", this.deliveryDate);
                this.consigneeSelectedValue = this.consigneeListOptions.find(
                  (consignee: any) =>
                    consignee.id === this.userData["consigneeId"]
                ) || { id: "", name: "" };
                this.getConsigneeLocationById(this.userData["consigneeId"]);
              }
            } catch (error) {
              console.error("Error parsing customer data:", error);
            }
          });
          this.filteredConsigneeListOptions = [...this.consigneeListOptions];
        } else {
          console.log("Hey you");
        }
      },
      error: (error) => {
        console.error("Error status:", error.status);
        console.error("Error message:", error.message);
      },
    });
  }
  onConsigneeSelected(event: any) {
    console.log("Selected value:", event.value); // This will log the selected object
    console.log("Consignee ID:", event.value.id); // Access a specific property
    this.getConsigneeLocationById(event.value.id);
  }
  getConsigneeLocationById(id: string) {
    this.deliveryLocationOptions = [];
    this.deliveryContactList = [];
    this.filteredConsigneeDeliveryLocationListOptions = [];
    let loginData = JSON.parse(localStorage.getItem("loginData")!);
    console.log("LoginData: ", loginData);
    this.apiService
      .get(`/api/orders/locations/consignee/${id}`, loginData.token)
      .subscribe({
        next: (response) => {
          console.log(
            `/api/orders/locations/consignee/${id}` +
              ",\n" +
              "Consignee_Location:",
            response.body.locations
          );
          if (response.status === 200) {
            response.body.locations.forEach((data: { [x: string]: any }) => {
              if (data["id"] != null) {
                try {
                  this.deliveryLocationOptions.push({
                    id: data["id"],
                    address: data["address"],
                    locationPin: data["locationPin"],
                    contacts: data["contacts"],
                  });
                  console.log(
                    "this.deliveryLocationOptions: ",
                    this.deliveryLocationOptions
                  );
                  if (this.isEdit == "true") {
                    this.deliveryContactList = [];
                    this.deliveryLocationSelectedValue =
                      this.deliveryLocationOptions.find(
                        (location: any) =>
                          location.id === this.userData["deliveryLocationId"]
                      ) || {
                        id: "",
                        address: "",
                        locationPin: "",
                        contacts: [],
                      };
                    this.deliveryLocationSelectedValue.contacts.forEach(
                      (contact: any) => {
                        this.deliveryContactList.push({
                          name: contact["name"],
                          phone: contact["phone"],
                          position: contact["position"],
                        });
                        this.showDeliveryContact = true;
                      }
                    );
                  }
                } catch (error) {
                  console.error("Error parsing consignee data:", error);
                }
              }
            });
            this.filteredConsigneeDeliveryLocationListOptions = [
              ...this.deliveryLocationOptions,
            ];
          } else {
            console.log("Hey you");
          }
        },
        error: (error) => {
          console.error("Error status:", error.status);
          console.error("Error message:", error.message);
        },
      });
  }
  onConsigneeLocationSelected(event: any) {
    console.log("Selected value:", event.value); // This will log the selected object
    console.log("Location ID:", event.value.id); // Access a specific property
    this.deliveryLocationSelectedValue = event.value;
    event.value.contacts.forEach((contact: any) => {
      this.deliveryContactList.push({
        name: contact["name"],
        phone: contact["phone"],
        position: contact["position"],
      });
    });
    if (this.deliveryContactList.length > 0) {
      this.showDeliveryContact = true;
    } else {
      this.showDeliveryContact = false;
    }
    console.log("this.deliveryContactList: ", this.deliveryContactList);
  }

  setDimensions() {
    console.log("Dimension Data: ", this.globalVariable.SETTING_DIMENSION);
    for (const data of this.globalVariable.SETTING_DIMENSION || []) {
      if (!this.dimensionOptions.some((r) => r["id"] === data.id)) {
        this.dimensionOptions.push(data);
      }
    }
  }
  setTypeOfTruck() {
    for (const data of this.globalVariable.SETTING_TRUCK_TYPES || []) {
      if (!this.truckTypes.some((r) => r["id"] === data.id)) {
        this.truckTypes.push(data);
      }
    }
  }
  onTruckTypeOpened(opened: boolean, index: number) {
    if (opened) {
      this.filterTruckTypesList[index] = [...this.truckTypes];
    }
  }
  setHandlingMethod() {
    for (const data of this.globalVariable.SETTING_HANDLING_METHODS || []) {
      if (!this.loadingMethodOptions.some((r) => r["id"] === data.id)) {
        this.loadingMethodOptions.push(data);
        this.filterLoadingMethodList.push(data);
      }
      if (!this.unloadingMethodOptions.some((r) => r["id"] === data.id)) {
        this.unloadingMethodOptions.push(data);
        this.filterUnloadingMethodList.push(data);
      }
    }
  }

  setPackagingTypes() {
    // Get packaging types from global variables (API)
    for (const data of this.globalVariable.PACKAGING_TYPES || []) {
      if (!this.packagingTypeOptions.some((r) => r["id"] === data.id)) {
        this.packagingTypeOptions.push(data);
      }
    }
  }

  setTemperatureControlOptions() {
    // Get temperature control options from global variables (API)
    for (const data of this.globalVariable.TEMPERATURE_CONTROL || []) {
      if (!this.temperatureControlOptions.some((r) => r["id"] === data.id)) {
        this.temperatureControlOptions.push(data);
      }
    }
  }

  setHandlingTypeOptions() {
    // Get handling type options from global variables (API)
    for (const data of this.globalVariable.HANDLING_TYPES || []) {
      if (!this.handlingTypeOptions.some((r) => r["id"] === data.id)) {
        this.handlingTypeOptions.push(data);
      }
    }
  }

  get pickupContact() {
    return this.contactForm.get("pickupContact") as FormArray;
  }

  get truckOrdered() {
    return this.orderTruckForm.get("truckOrdered") as FormArray;
  }

  // Get Delivery Contacts
  get deliveryContact() {
    return this.contactForm.get("deliveryContact") as FormArray;
  }

  onPickupDateChange(event: MatDatepickerInputEvent<Date>) {
    // this.pickupDate = event.value;
    if (event.value) {
      this.pickupDate = formatDate(event.value, "yyyy-MM-dd", "en-US");
    }
    console.log("Pickup Date:", this.pickupDate);
    if (
      this.deliveryDate &&
      this.pickupDate &&
      new Date(this.deliveryDate) < new Date(this.pickupDate)
    ) {
      this.deliveryDate = "";
      this.notification.warning(
        "Delivery Date has been cleared because it was before the new Pickup Date"
      );
    }
  }

  onDeliveryDateChange(event: MatDatepickerInputEvent<Date>) {
    if (event.value) {
      this.deliveryDate = formatDate(event.value, "yyyy-MM-dd", "en-US");
    }
    console.log("Delivery Date:", this.deliveryDate);
  }

  createContactForm(): FormGroup {
    return this.fb.group({
      name: ["", Validators.required],
      number: ["", Validators.required],
    });
  }
  createTruckForm(): FormGroup {
    return this.fb.group({
      truckType: [""],
      numberOfTruck: [""],
    });
  }

  addPickupContact() {
    this.pickupContact.push(this.createContactForm());
  }
  addTruckType() {
    this.truckOrdered.push(this.createTruckForm());
    // NEW line added:
    this.filterTruckTypesList[this.truckOrdered.length - 1] = [
      ...this.truckTypes,
    ];
  }

  // Add Delivery Contact
  addDeliveryContact() {
    this.deliveryContact.push(this.createContactForm());
  }

  // Remove Pickup Contact
  removePickupContact(index: number) {
    this.pickupContact.removeAt(index);
  }
  removeTruckType(index: number) {
    this.truckOrdered.removeAt(index);
    // NEW line added:
    this.filterTruckTypesList.splice(index, 1);
  }

  // Remove Delivery Contact
  removeDeliveryContact(index: number) {
    this.deliveryContact.removeAt(index);
  }

  // Get All Contacts (Pickup & Delivery)
  getAllContacts() {
    console.log(this.contactForm.value);
  }

  handleMapClickPickup(marker: any) {
    console.log("Pickup Marker:", marker);
    console.log(
      "Pickup => Lat: ",
      marker.latLng.lat(),
      " Long: ",
      marker.latLng.lng()
    );
  }
  handleMapClickDelivery(marker: any) {
    console.log("Delivery Marker:", marker);
    console.log(
      "Delivery => Lat: ",
      marker.latLng.lat(),
      " Long: ",
      marker.latLng.lng()
    );
  }

  handleLocationChanged(newLocation: google.maps.LatLngLiteral) {
    this.currentLocation = newLocation;
    console.log("Location received in parent:", this.currentLocation);
  }
  addCommodity() {
    this.commodityTotals.push({
      customerRef: "",
      description: "",
      quantity: "",
      packagingType: this.packagingTypeOptions[0] || { id: "", name: "" },
      length: "",
      width: "",
      height: "",
      volume: "",
      grossWeight: "",
      stackable: false,
      dangerousFlag: false,
      temperatureControl: this.temperatureControlOptions[0] || {
        id: "",
        name: "",
      },
      handlingType: this.handlingTypeOptions[0] || { id: "", name: "" },
    });
  }
  removeCommodity(index: number) {
    if (this.commodityTotals.length > 1) {
      this.commodityTotals.splice(index, 1);
    }
  }

  submitButton() {
    var commoditiesData: any[] = [];
    for (let i = 0; i < this.commodityTotals.length; i++) {
      commoditiesData[i] = {
        customerRef: this.commodityTotals[i].customerRef,
        description: this.commodityTotals[i].description,
        quantity: this.commodityTotals[i].quantity,
        packagingType: this.commodityTotals[i].packagingType["id"],
        lengthCm: this.commodityTotals[i].length,
        widthCm: this.commodityTotals[i].width,
        heightCm: this.commodityTotals[i].height,
        volumeM3: this.commodityTotals[i].volume,
        grossWeightKg: this.commodityTotals[i].grossWeight,
        stackable: this.commodityTotals[i].stackable,
        dgFlag: this.commodityTotals[i].dangerousFlag,
        temperatureControlId: this.commodityTotals[i].temperatureControl["id"],
        handlingTypeId: this.commodityTotals[i].handlingType["id"],
      };
    }
    var truckTypeData = [{ truckType: "", numberOfTruck: "" }];
    for (let i = 0; i < this.orderTruckForm.value.truckOrdered.length; i++) {
      truckTypeData[i] = {
        truckType: this.orderTruckForm.value.truckOrdered[i].truckType["id"],
        numberOfTruck: this.orderTruckForm.value.truckOrdered[i].numberOfTruck,
      };
    }

    console.log("CustomerNameId: ", this.customerSelectedValue.id);
    console.log("PickUp Location: ", this.pickUpLocationSelectedValue.id);
    console.log("ConsigneeNameId: ", this.consigneeSelectedValue.id);
    console.log("Delivery Location: ", this.deliveryLocationSelectedValue.id);
    console.log("Commodity: ", this.commodityTotals);
    console.log("Total Truck Ordered: ", this.totalTruckOrdered);
    // console.log("Loading Method: ", this.loadingMethodSelectedValue.id);
    // console.log("Unloading Method: ", this.unloadingMethodSelectedValue.id);
    console.log(
      "Pickup Date: ",
      formatDate(this.pickupDate, "yyyy-MM-dd", "en-US")
    );
    console.log("Delivery Date: ", this.deliveryDate);
    console.log("Truck Ordered: ", truckTypeData);
    console.log("Dimension: ", this.dimensionSelectedValue.id);
    let loginData = JSON.parse(localStorage.getItem("loginData")!);
    if (this.customerSelectedValue.name.length < 1) {
      this.notification.warning("Please select Customer na");
      return;
    }
    if (this.pickUpLocationSelectedValue.id.length < 1) {
      this.notification.warning("Please select PickUp Location");
      return;
    }
    if (this.consigneeSelectedValue.name.length < 1) {
      this.notification.warning("Please select Consignee");
      return;
    }
    if (this.deliveryLocationSelectedValue.id.length < 1) {
      this.notification.warning("Please select Delivery Location");
      return;
    }
    if (commoditiesData.length < 1) {
      this.notification.warning("Please add Commodity");
      return;
    }
    if (commoditiesData.some((c) => !c.customerRef || c.customerRef.trim().length < 1)) {
      this.notification.warning("Please enter Customer Ref for all commodities");
      return;
    }
    if (truckTypeData.length < 1) {
      this.notification.warning("Please enter Total Truck Ordered");
      return;
    }
    if (this.pickupDate.length < 1) {
      this.notification.warning("Please select Pickup Date");
      return;
    }
    if (this.deliveryDate.length < 1) {
      this.notification.warning("Please select Delivery Date");
      return;
    }
    if (new Date(this.deliveryDate) < new Date(this.pickupDate)) {
      this.notification.warning("Delivery Date must be after Pickup Date");
      return;
    }
    if (this.orderTruckForm.value.truckOrdered.length < 1) {
      this.notification.warning("Please add Truck Ordered");
      return;
    }

    const sumOfTrucks = this.orderTruckForm.value.truckOrdered.reduce(
      (sum: number, t: any) => sum + (Number(t.numberOfTruck) || 0),
      0
    );
    if (Number(this.totalTruckOrdered) !== sumOfTrucks) {
      this.truckCountMismatch = true;
      this.notification.warning(
        `Total Trucks Required (${this.totalTruckOrdered}) does not match the sum of trucks in the list (${sumOfTrucks})`
      );
      return;
    }
    this.truckCountMismatch = false;

    let jsonData = {
      customerId: this.customerSelectedValue.id,
      pickupLocationId: this.pickUpLocationSelectedValue.id,
      pickupDate: this.pickupDate,
      consigneeId: this.consigneeSelectedValue.id,
      deliveryLocationId: this.deliveryLocationSelectedValue.id,
      deliveryDate: this.deliveryDate,
      cargoItems: commoditiesData,
      numberOfTrucksOrder: this.totalTruckOrdered,
      truckTypesLoaded: truckTypeData,
    };
    console.log("Booking data: ", jsonData);
    let endpoint = "";
    try {
      if (this.isDraft) {
        endpoint = "/api/orders/drafts/"+ this.userData["id"] +"/submit";
          this.apiService.post(endpoint, jsonData, loginData.token).subscribe({
            next: (response) => {
              console.log("HTTP Status:", response.status);
              console.log("Response Body:", response.body);
              if (response.status === 200 || response.status === 201) {
                this.notification.success("Booking Order successfully");
                this.router.navigate(["/home/booking-list"]);
              } else {
                this.notification.error(
                  "Booking Order failed. Please try again."
                );
              }
            },
            error: (error) => {
              console.error("Error:", error);
            },
          });
      } else {
        if (this.isEdit == "true") {
          endpoint = "/api/orders/" + this.userData["id"];
          this.apiService.put(endpoint, jsonData, loginData.token).subscribe({
            next: (response: HttpResponse<any>) => {
              console.log("HTTP Status:", response.status);
              console.log(
                "/api/orders/edit" + ",\n" + "Edit_Response:",
                response.body
              );

              if (response.status === 200) {
                this.notification.success("Update Booking Order successfully");
                this.router.navigate(["/home/booking-list"]);
              } else {
                this.notification.error(
                  "Update Booking Order failed. Please try again."
                );
              }
            },
            error: (err) => {
              console.error("Error:", err);
              this.notification.error(`Booking order failed: ${err.message}`);
            },
          });
        } else {
          endpoint = "/api/orders";
          this.apiService.post(endpoint, jsonData, loginData.token).subscribe({
            next: (response) => {
              console.log("HTTP Status:", response.status);
              console.log("Response Body:", response.body);
              if (response.status === 200 || response.status === 201) {
                this.notification.success("Booking Order successfully");
                this.router.navigate(["/home/booking-list"]);
              } else {
                this.notification.error(
                  "Booking Order failed. Please try again."
                );
              }
            },
            error: (error) => {
              console.error("Error:", error);
            },
          });
        }
      }
    } catch (error) {
      this.notification.error(`Booking order failed: ${error}`);
    }
    console.log("endpoint: ", endpoint);
  }
  enableEditMode() {
    this.isReadOnly = false;
  }

  saveDraftButton() {
    var commoditiesData: any[] = [];
    for (let i = 0; i < this.commodityTotals.length; i++) {
      commoditiesData[i] = {
        customerRef: this.commodityTotals[i].customerRef || "",
        description: this.commodityTotals[i].description,
        quantity: this.commodityTotals[i].quantity || 0,
        packagingType: this.commodityTotals[i].packagingType["id"],
        lengthCm: this.commodityTotals[i].length || 0,
        widthCm: this.commodityTotals[i].width || 0,
        heightCm: this.commodityTotals[i].height || 0,
        volumeM3: this.commodityTotals[i].volume || 0,
        grossWeightKg: this.commodityTotals[i].grossWeight || 0,
        stackable: this.commodityTotals[i].stackable || false,
        dgFlag: this.commodityTotals[i].dangerousFlag || false,
        temperatureControl: this.commodityTotals[i].temperatureControl["id"] || "",
        handlingType: this.commodityTotals[i].handlingType["id"] || "",
      };
    }
    var truckTypeData = [{ truckType: "", numberOfTruck: "" }];
    for (let i = 0; i < this.orderTruckForm.value.truckOrdered.length; i++) {
      truckTypeData[i] = {
        truckType: this.orderTruckForm.value.truckOrdered[i].truckType["id"],
        numberOfTruck: this.orderTruckForm.value.truckOrdered[i].numberOfTruck,
      };
    }

    console.log("CustomerNameId: ", this.customerSelectedValue.id);
    let loginData = JSON.parse(localStorage.getItem("loginData")!);
    if (this.customerSelectedValue.name.length < 1) {
      this.notification.warning("Please select Customer name");
      return;
    }

    let jsonData = {
      customerId: this.customerSelectedValue.id,
      pickupLocationId: this.pickUpLocationSelectedValue.id ?? "",
      pickupDate: this.pickupDate ?? "",
      consigneeId: this.consigneeSelectedValue.id ?? "",
      deliveryLocationId: this.deliveryLocationSelectedValue.id ?? "",
      deliveryDate: this.deliveryDate ?? "",
      cargoItems: commoditiesData ?? [],
      numberOfTrucksOrder: this.totalTruckOrdered ?? "",
      // "loadingMethod": this.loadingMethodSelectedValue.id ?? "",
      // "unloadingMethod": this.unloadingMethodSelectedValue.id ?? "",
      truckTypesLoaded: truckTypeData ?? [],
    };
    console.log("Booking data: ", jsonData);
    let endpoint = "";
    try {
      if (this.isEdit == "true") {
        endpoint = "/api/orders/drafts/" + this.userData["id"];
        this.apiService.put(endpoint, jsonData, loginData.token).subscribe({
          next: (response: HttpResponse<any>) => {
            console.log("HTTP Status:", response.status);
            console.log(
              "/api/orders/edit" + ",\n" + "Edit_Response:",
              response.body
            );

            if (response.status === 200) {
              this.notification.success("Update Booking Order successfully");
              this.router.navigate(["/home/booking-list"]);
            } else {
              this.notification.error(
                "Update Booking Order failed. Please try again."
              );
            }
          },
          error: (err) => {
            console.error("Error:", err);
            this.notification.error(`Booking order failed: ${err.message}`);
          },
        });
      } else {
        endpoint = "/api/orders/drafts";
        this.apiService.post(endpoint, jsonData, loginData.token).subscribe({
          next: (response) => {
            console.log("HTTP Status:", response.status);
            console.log("Response Body:", response.body);
            if (response.status === 200 || response.status === 201) {
              this.notification.success("Booking Order successfully");
              this.router.navigate(["/home/booking-list"]);
            } else {
              this.notification.error(
                "Booking Order failed. Please try again."
              );
            }
          },
          error: (error) => {
            console.error("Error:", error);
          },
        });
      }
    } catch (error) {
      this.notification.error(`Booking order failed: ${error}`);
    }
    console.log("endpoint: ", endpoint);
  }
}
